import React, { useState } from 'react';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { TeamMemberPicker } from '../../../../shared/components/group-ui/team-member-picker/team-member-picker.component';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import {
  LeadBulkAssignResultModel,
  LeadBulkAssignSummaryModel
} from '../../../../shared/models/lead.model';
import { bulkAssignLeads, bulkAutoAssignLeads } from '../../../../shared/services/data/lead.data';

interface BulkAssignBarProps {
  selectedIds: string[];
  maxLeads: number;
  onClear: () => void;
  /** Called after any bulk action, so the list reloads. */
  onDone: () => void;
}

/**
 * The bar that appears once leads are ticked.
 *
 * Two actions, deliberately distinct. **Assign to** hands the selection to one
 * person a manager chose. **Auto-assign** runs the routing rules, which only
 * touch leads nobody holds — re-running routing over an agent's book would
 * move work people are mid-conversation on, so the service skips those and
 * says so rather than silently doing less than the button implied.
 *
 * The result is reported per lead, not as a single toast. A batch of two
 * hundred where six failed on an active-lead cap is a different thing from one
 * that worked, and collapsing both into "done" is how the six get lost.
 */
export const BulkAssignBar: React.FC<BulkAssignBarProps> = ({
  selectedIds,
  maxLeads,
  onClear,
  onDone
}) => {
  const [assignee, setAssignee] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<LeadBulkAssignSummaryModel | undefined>();

  const overLimit: boolean = selectedIds.length > maxLeads;

  const run = (action: () => Promise<LeadBulkAssignSummaryModel | unknown>) => {
    setBusy(true);
    setSummary(undefined);
    action().then((result) => {
      setBusy(false);
      if (isErrorInfo(result as never)) return;
      setSummary(result as LeadBulkAssignSummaryModel);
      onDone();
    });
  };

  const problems: LeadBulkAssignResultModel[] = (summary?.leads ?? []).filter(
    (lead: LeadBulkAssignResultModel) => lead.status !== 'assigned'
  );

  return (
    <section className="mb-3 rounded-2xl border border-creams-600 bg-white p-4 shadow-md">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-gilroy-bold text-base text-neutral-500">
          {selectedIds.length} selected
        </span>

        <div className="min-w-[260px]">
          {/*
            Team members only. A lead assigned outside the team tree is
            invisible to the person holding it, and the service refuses it —
            offering the name would be an error waiting to happen.
          */}
          <TeamMemberPicker value={assignee} onChange={setAssignee} placeholder="Assign to…" />
        </div>

        <Button
          type={ButtonType.primary}
          disabled={!assignee || busy || overLimit}
          tracking={{ event: 'record_saved', entity: 'lead', action: 'assign', outcome: 'success' }}
          onClick={() => assignee && run(() => bulkAssignLeads(selectedIds, assignee))}
        >
          {busy ? 'Assigning…' : 'Assign'}
        </Button>

        <Button
          type={ButtonType.gray}
          disabled={busy || overLimit}
          onClick={() => run(() => bulkAutoAssignLeads(selectedIds))}
        >
          Auto-assign by rules
        </Button>

        <Button type={ButtonType.gray} disabled={busy} onClick={onClear}>
          Clear
        </Button>
      </div>

      {overLimit ? (
        <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
          That is {selectedIds.length} leads and the limit is {maxLeads}. Narrow the filter or work
          through them in batches.
        </p>
      ) : null}

      {summary ? (
        <div className="mt-3">
          <p className="text-sm text-neutral-500">
            {summary.assigned} assigned
            {summary.skipped ? `, ${summary.skipped} already held by someone` : ''}
            {summary.failed ? `, ${summary.failed} could not be` : ''}.
          </p>

          {problems.length ? (
            <ul className="mt-2 max-h-[160px] overflow-y-auto text-sm text-neutral-400">
              {problems.map((lead: LeadBulkAssignResultModel) => (
                <li key={lead.leadId} className="border-t border-creams-500 py-1.5">
                  <span className="text-neutral-500">
                    {lead.shortId ? `#${lead.shortId}` : lead.leadId}
                  </span>{' '}
                  — {lead.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
